import type * as CV from "./framework.d"
import { Port } from "./port"

export class Graph<I extends unknown[], O extends unknown[] = void[]> implements CV.Graph<I, O> {
  private readonly _ins = new Map<number, GraphInput<I[number]>>()
  private readonly _outs = new Map<number, GraphOutput<O[number]>>()
  private readonly _nodes = new Set<Node<unknown[], unknown[]>>()
  private readonly _errors: Error[] = [];

  in<T extends number>(index: T): CV.GraphInput<I[T]> {
    if (!this._ins.has(index)) this._ins.set(index, new GraphInput())
    return this._ins.get(index)!
  }

  out<T extends number>(index: T): CV.GraphOutput<O[T]> {
    if (!this._outs.has(index)) this._outs.set(index, new GraphOutput())
    return this._outs.get(index)!
  }

  node<I extends unknown[], O extends unknown[] = void[]>(config: CV.NodeConfig<I, O>) {
    const node = new Node<I, O>({
      ...config,
      onError: (error) => {
        this._errors.push(error)
        this._ins.forEach((input) => (input as Port<unknown>)["close"]())
        this._outs.forEach((out) => (out as Port<unknown>)["close"]())
      },
    })
    this._nodes.add(node as unknown as Node<unknown[], unknown[]>)
    return node
  }

  hasError() {
    return this._errors.length > 0
  }

  getComposedErrors() {
    return this._errors
  }

  destroy() {
    this._nodes.forEach((n) => n.destroy())

    this._ins.clear(), this._outs.clear(), this._nodes.clear()
  }
}

export class GraphInput<T> extends Port<T> implements CV.GraphInput<T> {
  override write(...args: Parameters<Port<T>["write"]>) {
    return super.write(...args)
  }

  override connect(target: NodeInput<T>) {
    return super.connect(target)
  }
}

export class GraphOutput<T> extends Port<T> implements CV.GraphOutput<T> {
  override connect(target: GraphInput<T>) {
    return super.connect(target)
  }

  override async *data() {
    yield* super.data()
  }
}

export class Node<I extends unknown[], O extends unknown[] = void[]> implements CV.Node<I, O> {
  private readonly _ins = new Map<number, NodeInput<I[number]>>()
  private readonly _outs = new Map<number, NodeOutput<O[number]>>()
  private readonly _iterators = new WeakMap<
    NodeInput<unknown>,
    ReturnType<NodeInput<unknown>["data"]>
  >()
  private readonly _values = new Map<number, I[number]>()
  private readonly _process: CV.NodeConfig<I, O>["process"]
  private readonly _destroy: CV.NodeConfig<I, O>["destroy"]

  private readonly _onError?: (error: Error) => void

  constructor(config: CV.NodeConfig<I, O>) {
    this._process = config.process
    this._destroy = config.destroy
    this._onError = config.onError
  }

  in<T extends number>(index: T): NodeInput<I[T]> {
    if (!this._ins.has(index)) {
      const input = new NodeInput()
      this._ins.set(index, input)

      const pull = async (input: NodeInput<unknown>, index: number) => {
        if (!this._iterators.has(input)) this._iterators.set(input, input["data"]())
        const it = this._iterators.get(input)!

        const { value } = await it.next()
        this._values.set(index, value)

        if (this._values.size !== this._ins.size) return

        const values = new Map(this._values) // shallow copy
        this._values.clear()

        try {
          await this._process({
            in: (index) => values.get(index),
            out: (index, data) => {
              this._outs.get(index)?.["write"](data)
            },
          })
        } catch (error) {
          this._onError?.(error as Error)
          return
        }

        this._ins.forEach(pull)
      }

      pull(input, index)
    }

    return this._ins.get(index)!
  }

  out<T extends number>(index: T): CV.NodeOutput<O[T]> {
    if (!this._outs.has(index)) this._outs.set(index, new NodeOutput())
    return this._outs.get(index)!
  }

  destroy() {
    this._destroy?.()
  }
}

export class NodeInput<T> extends Port<T> implements CV.NodeInput {}

export class NodeOutput<T> extends Port<T> implements CV.NodeOutput<T> {
  override connect(target: NodeInput<T>): ReturnType<Port<T>["connect"]>
  override connect(target: GraphOutput<T>): ReturnType<Port<T>["connect"]>
  override connect(target: NodeInput<T> | GraphOutput<T>) {
    return super.connect(target)
  }
}
